import {Button, Menu, MenuItem} from '@mui/material';
import {
  ComponentProps,
  JSXElementConstructor,
  ReactNode,
  useMemo,
  useState,
} from 'react';

import BeltItemsPurgeDeviceButton from './BeltItemsPurgeDeviceButton';
import BeltItemsPurgeEventButton from './BeltItemsPurgeEventButton';

type ComponentType = keyof JSX.IntrinsicElements | JSXElementConstructor<any>;

interface Props<T extends ComponentType> {
  beltIds?: number[];
  component?: T;
  componentProps?: ComponentProps<T>;
  children?: ReactNode;
  onDone?: () => void;
}

const BeltItemsPurgeButton = <T extends ComponentType = typeof Button>({
  beltIds,
  component,
  componentProps,
  children,
  onDone,
}: Props<T>) => {
  const Component = component ?? Button;
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement>();
  const menuOpen = !!menuAnchorEl;

  const count = useMemo(() => {
    return beltIds?.length ?? 0;
  }, [beltIds]);

  const string = useMemo(() => {
    return `${count} Belt${count > 1 ? 's' : ''}`;
  }, [count]);

  const purgeAllTitle = useMemo(() => {
    return `Device (${string})`;
  }, [count]);

  const purgeTransactionTitle = useMemo(() => {
    return `Transactions (${string})`;
  }, [count]);

  const purgeAllButtonTitle = useMemo(() => {
    return `Purge Device (${string})`;
  }, [count]);

  const purgeTransactionButtonTitle = useMemo(() => {
    return `Purge Transactions (${string})`;
  }, [count]);

  return (
    <>
      <Component
        {...componentProps}
        onClick={(event) => setMenuAnchorEl(event.currentTarget)}
      >
        {children}
      </Component>

      <Menu
        anchorEl={menuAnchorEl}
        open={menuOpen}
        onBackdropClick={() => setMenuAnchorEl(undefined)}
      >
        <BeltItemsPurgeDeviceButton
          beltIds={beltIds}
          buttonTitle={purgeAllButtonTitle}
          component={MenuItem}
          onDone={() => {
            setMenuAnchorEl(undefined);
            onDone?.();
          }}
        >
          {purgeAllTitle}
        </BeltItemsPurgeDeviceButton>

        <BeltItemsPurgeEventButton
          beltIds={beltIds}
          buttonTitle={purgeTransactionButtonTitle}
          component={MenuItem}
          onDone={() => {
            setMenuAnchorEl(undefined);
            onDone?.();
          }}
        >
          {purgeTransactionTitle}
        </BeltItemsPurgeEventButton>
      </Menu>
    </>
  );
};

export default BeltItemsPurgeButton;
